import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Bot, X, Send, Loader2, ShieldCheck, BadgeCheck, ExternalLink,
  MessageCircleQuestion, Phone, User as UserIcon, MessageSquare,
  Calendar as CalendarIcon, Clock, CheckCircle2, ChevronLeft,
} from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";

const SESSION_KEY = "inprop_ai_session";
const MESSAGES_KEY = "inprop_ai_messages";
const OPENED_ONCE_KEY = "inprop_ai_opened";

function newSessionId() {
  return "sess_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

function initSession() {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = newSessionId();
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

// Extract `/agent/:slug` from any pathname (works both inside and outside router).
function agentSlugFromPath(pathname) {
  const m = /^\/agent\/([^/]+)/.exec(pathname || "");
  return m ? m[1] : null;
}

export default function InPropAIChat() {
  const location = useLocation();
  const params = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "owner";
  const agentSlug = params?.slug || agentSlugFromPath(location.pathname);

  const [open, setOpen] = useState(false);
  const [sid] = useState(initSession);
  const [messages, setMessages] = useState([]);
  const [session, setSession] = useState({ mode: "national", agent: null });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [used, setUsed] = useState(0);
  const [cap, setCap] = useState(10);

  // Multi-step lead: idle → name → phone → whatsapp → done
  const [leadStep, setLeadStep] = useState("idle");
  const [leadForm, setLeadForm] = useState({ name: "", phone: "", whatsapp: "" });

  // Site visit booking: idle → date → time → contact → confirm → done
  const [visitStep, setVisitStep] = useState("idle");
  const [visitForm, setVisitForm] = useState({
    date: null, time: "", name: "", phone: "", whatsapp: "", notes: "", project_ref: "",
  });
  const [visitBusy, setVisitBusy] = useState(false);
  const [availability, setAvailability] = useState(null); // {weekdays, time_slots, blackout_dates}

  // Fetch this agent's availability once we know the slug (also on national mode we skip).
  useEffect(() => {
    if (!agentSlug) { setAvailability(null); return; }
    (async () => {
      try {
        const { data } = await api.get("/inprop-ai/availability", { params: { agent_slug: agentSlug } });
        setAvailability({
          weekdays: data.weekdays || [1, 2, 3, 4, 5, 6],
          time_slots: data.time_slots || [],
          blackout_dates: data.blackout_dates || [],
        });
      } catch {
        setAvailability(null);
      }
    })();
  }, [agentSlug]);

  const PULSE_MS = 10000;
  const [pulse, setPulse] = useState(() => !sessionStorage.getItem(OPENED_ONCE_KEY));
  const scroller = useRef(null);

  // ─── Init session (greeting depends on agent slug) ──────────────────
  const initGreeting = useCallback(async () => {
    try {
      const { data } = await api.post("/inprop-ai/session-init", { agent_slug: agentSlug || null });
      setSession({ mode: data.mode, agent: data.agent });
      setMessages([{ role: "bot", text: data.greeting, greeting: true }]);
    } catch (_e) {
      setMessages([{
        role: "bot",
        text: "Hi, I'm InProp AI — India's Authorised Property Portal advisor. How can I help you today?",
        greeting: true,
      }]);
    }
  }, [agentSlug]);

  useEffect(() => {
    initGreeting();
    // Clear stored messages so the greeting always reflects current agent context.
    sessionStorage.removeItem(MESSAGES_KEY);
  }, [initGreeting]);

  useEffect(() => {
    if (!pulse) return;
    const t = setTimeout(() => setPulse(false), PULSE_MS);
    return () => clearTimeout(t);
  }, [pulse]);

  const handleToggle = () => {
    if (!open) {
      sessionStorage.setItem(OPENED_ONCE_KEY, "1");
      setPulse(false);
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages, open, leadStep]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/inprop-ai/chat", {
        session_id: sid,
        message: text,
        agent_slug: agentSlug || null,
      });
      setUsed(data.messages_used || 0);
      setCap(data.messages_cap || 10);
      setMessages((m) => [...m, {
        role: "bot",
        text: data.reply,
        hits: data.context_hits,
        citations: data.citations || [],
        verified: !!data.verified,
        live_verified: !!data.live_verified,
        live_fetch: data.live_fetch || [],
        grounded_hits: data.grounded_hits || 0,
        grounding: data.grounding || [],
      }]);
      if (data.lead_prompt && leadStep === "idle" && visitStep === "idle") {
        setLeadStep("name");
      }
      if (data.visit_prompt && visitStep === "idle") {
        setVisitStep("date");
      }
      if (data.lead_captured) {
        setLeadStep("done");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Something went wrong. Please retry.";
      setMessages((m) => [...m, { role: "bot", text: detail, error: true }]);
    } finally {
      setBusy(false);
    }
  };

  const advanceLead = () => {
    if (leadStep === "name") {
      if (!leadForm.name.trim()) return;
      setLeadStep("phone");
    } else if (leadStep === "phone") {
      if (!leadForm.phone.trim() || leadForm.phone.replace(/\D/g, "").length < 10) {
        toast.error("Please enter a valid 10-digit phone number");
        return;
      }
      setLeadStep("whatsapp");
    } else if (leadStep === "whatsapp") {
      submitLead();
    }
  };

  const submitLead = async () => {
    try {
      const { data } = await api.post("/inprop-ai/lead", {
        session_id: sid,
        name: leadForm.name.trim(),
        phone: leadForm.phone.trim(),
        whatsapp: leadForm.whatsapp.trim() || "same",
        agent_slug: agentSlug || null,
      });
      const contactName = session.agent?.name || "an authorised advisor";
      setMessages((m) => [...m, {
        role: "bot",
        text: `Thanks ${leadForm.name}! ${contactName} will reach out at ${leadForm.phone} shortly.`,
      }]);
      setLeadStep("done");
      toast.success("Details captured — talk soon!");
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Please recheck your phone number.");
    }
  };

  const AGENT = session.agent;
  const isAgentMode = session.mode === "agent" && AGENT;

  return (
    <>
      {/* ─── Floating bubble ─── */}
      <button
        type="button"
        onClick={handleToggle}
        className={`fixed bottom-24 right-5 z-[60] w-14 h-14 rounded-full shadow-lg shadow-sg-navy/25 flex items-center justify-center transition-all hover:scale-105 ${
          open ? "bg-sg-navy text-white" : "bg-sg-gold text-sg-navy"
        }`}
        data-testid="inprop-ai-toggle"
        aria-label="Open InProp AI advisor"
      >
        {!open && pulse && (
          <>
            <span className="absolute inset-0 rounded-full bg-sg-gold/60 animate-ping" aria-hidden="true" />
            <span className="absolute -inset-1 rounded-full border-2 border-sg-gold/50 animate-pulse" aria-hidden="true" />
          </>
        )}
        {open ? (
          <X className="w-5 h-5 relative" />
        ) : (
          <>
            <Bot className="w-6 h-6 relative" strokeWidth={2.25} />
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-sg-navy text-sg-gold flex items-center justify-center shadow ring-2 ring-white">
              <BadgeCheck className="w-2.5 h-2.5" />
            </span>
          </>
        )}
      </button>

      {/* ─── Concierge Panel ─── */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-44 right-5 z-[60] w-[94vw] max-w-[440px] h-[72vh] max-h-[700px] bg-white rounded-3xl border border-sg-line shadow-2xl shadow-sg-navy/20 flex flex-col overflow-hidden"
            data-testid="inprop-ai-panel"
          >
            {/* Concierge Header */}
            <div className="relative bg-gradient-to-br from-sg-navy via-sg-navy to-[#0a1a3a] text-white overflow-hidden">
              <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(circle_at_20%_20%,#F5B733_2px,transparent_2px)] [background-size:14px_14px]" aria-hidden="true" />
              <div className="relative p-4 flex items-center gap-3">
                <div className="relative">
                  <div className="w-11 h-11 rounded-full bg-sg-gold text-sg-navy flex items-center justify-center flex-shrink-0 shadow-lg shadow-sg-gold/40">
                    <Bot className="w-5 h-5" strokeWidth={2.25} />
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-400 border-2 border-sg-navy" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <div className="text-sm font-semibold tracking-tight">InProp AI</div>
                    <BadgeCheck className="w-3.5 h-3.5 text-sg-gold" />
                  </div>
                  <div className="text-[10px] text-white/70 uppercase tracking-[0.18em]">
                    {isAgentMode
                      ? `Assisting ${AGENT.name}`
                      : "India's Authorised Property Advisor"}
                  </div>
                </div>
                <div className="text-[10px] font-mono text-white/45 tabular-nums">{used}/{cap}</div>
              </div>

              {/* Agent context strip */}
              {isAgentMode && (
                <div className="relative px-4 pb-3 flex items-center gap-2 text-[11px] text-white/85 border-t border-white/10 pt-2.5">
                  {AGENT.picture ? (
                    <img src={AGENT.picture} alt="" className="w-6 h-6 rounded-full border border-white/30 object-cover" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-white/15 flex items-center justify-center">
                      <UserIcon className="w-3 h-3" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1 truncate">
                    <span className="font-medium">{AGENT.name}</span>
                    {AGENT.agency_name && <span className="text-white/55"> · {AGENT.agency_name}</span>}
                  </div>
                  {AGENT.maharera_number && (
                    <span className="px-2 py-0.5 rounded-full bg-white/10 text-[9px] font-mono uppercase tracking-wider text-sg-gold border border-white/15">
                      RERA {AGENT.maharera_number}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Messages */}
            <div
              ref={scroller}
              className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gradient-to-b from-sg-cream-soft/20 to-white"
              data-testid="inprop-ai-messages"
            >
              {messages.map((m, i) => (
                <MessageBubble key={i} m={m} isAgentMode={isAgentMode} agentName={AGENT?.name} isAdmin={isAdmin} />
              ))}
              {busy && (
                <div className="flex items-center gap-2 text-xs text-sg-mute px-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>InProp AI is verifying against RERA registries…</span>
                </div>
              )}

              {/* Multi-step lead capture UI */}
              {leadStep !== "idle" && leadStep !== "done" && visitStep === "idle" && (
                <LeadFlow
                  step={leadStep}
                  form={leadForm}
                  setForm={setLeadForm}
                  onAdvance={advanceLead}
                  contactName={AGENT?.name}
                />
              )}

              {/* Site visit booking UI */}
              {visitStep !== "idle" && visitStep !== "done" && (
                <VisitFlow
                  step={visitStep}
                  form={visitForm}
                  setForm={setVisitForm}
                  busy={visitBusy}
                  availability={availability}
                  onBack={() => {
                    const back = { time: "date", contact: "time", confirm: "contact" }[visitStep];
                    if (back) setVisitStep(back);
                    else setVisitStep("idle");
                  }}
                  onAdvance={async () => {
                    if (visitStep === "date") {
                      if (!visitForm.date) { toast.error("Pick a date"); return; }
                      setVisitStep("time");
                    } else if (visitStep === "time") {
                      if (!visitForm.time) { toast.error("Pick a time slot"); return; }
                      setVisitStep("contact");
                    } else if (visitStep === "contact") {
                      if (!visitForm.name.trim()) { toast.error("Please share your name"); return; }
                      if (visitForm.phone.replace(/\D/g, "").length < 10) { toast.error("Valid 10-digit phone required"); return; }
                      setVisitStep("confirm");
                    } else if (visitStep === "confirm") {
                      setVisitBusy(true);
                      try {
                        const iso = toISODate(visitForm.date);
                        const { data } = await api.post("/inprop-ai/book-visit", {
                          session_id: sid,
                          name: visitForm.name.trim(),
                          phone: visitForm.phone.trim(),
                          whatsapp: visitForm.whatsapp.trim() || "same",
                          preferred_date: iso,
                          time_slot: visitForm.time,
                          project_ref: visitForm.project_ref || null,
                          notes: visitForm.notes || null,
                          agent_slug: agentSlug || null,
                        });
                        setVisitStep("done");
                        const contactName = data.agent_name || session.agent?.name || "our advisor";
                        setMessages((m) => [...m, {
                          role: "bot",
                          text: `Booked! ${contactName} will meet you on ${formatDatePretty(visitForm.date)} at ${visitForm.time}. Confirmation shared to WhatsApp ${visitForm.whatsapp || visitForm.phone}.`,
                        }]);
                        toast.success("Site visit booked — talk soon!");
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Could not book — please recheck details.");
                      } finally {
                        setVisitBusy(false);
                      }
                    }
                  }}
                  onCancel={() => { setVisitStep("idle"); setVisitForm({ date: null, time: "", name: "", phone: "", whatsapp: "", notes: "", project_ref: "" }); }}
                  contactName={AGENT?.name}
                />
              )}
            </div>

            {/* Concierge Footer */}
            <div className="border-t border-sg-line bg-white">
              {/* Suggested prompts (only on first turn) */}
              {messages.length <= 1 && !busy && (
                <div className="px-3 pt-2.5 pb-1 flex flex-wrap gap-1.5">
                  {(isAgentMode
                    ? [
                        { label: "Show me 2BHK options", type: "input" },
                        { label: "Possession in 2026?", type: "input", full: "What projects have possession in 2026?" },
                        { label: "Book a site visit", type: "visit" },
                      ]
                    : [
                        { label: "Best projects in Baner", type: "input" },
                        { label: "Kharadi vs Hinjewadi", type: "input" },
                        { label: "MahaRERA-verified 3BHK", type: "input" },
                      ]
                  ).map((p) => (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => {
                        if (p.type === "visit") {
                          if (visitStep === "idle") setVisitStep("date");
                        } else {
                          setInput(p.full || p.label);
                        }
                      }}
                      className="text-[11px] px-2.5 py-1 rounded-full bg-sg-cream-soft/70 border border-sg-line hover:border-sg-gold text-sg-navy transition-colors"
                      data-testid={`inprop-ai-prompt-${p.label.slice(0,12).replace(/\s/g,'-').toLowerCase()}`}
                    >
                      {p.type === "visit"
                        ? <CalendarIcon className="w-3 h-3 inline mr-1 -mt-0.5" />
                        : <MessageCircleQuestion className="w-3 h-3 inline mr-1 -mt-0.5" />}
                      {p.label}
                    </button>
                  ))}
                </div>
              )}

              <div className="p-3">
                <div className="flex items-center gap-2 bg-sg-cream-soft/50 rounded-full pl-4 pr-1 py-1 border border-sg-line focus-within:border-sg-gold transition-colors">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                    disabled={busy || used >= cap}
                    placeholder={used >= cap ? "Session limit reached — refresh to start again" : (isAgentMode ? `Ask about ${AGENT.name}'s listings…` : "Ask about any Indian project…")}
                    className="flex-1 bg-transparent text-sm text-sg-navy placeholder:text-sg-mute focus:outline-none disabled:opacity-50"
                    data-testid="inprop-ai-input"
                  />
                  <button
                    type="button"
                    onClick={send}
                    disabled={busy || used >= cap || !input.trim()}
                    className="h-9 w-9 rounded-full bg-sg-navy hover:bg-sg-navy/90 text-white disabled:opacity-40 flex items-center justify-center flex-shrink-0"
                    data-testid="inprop-ai-send"
                    aria-label="Send message"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
                {/* Concierge credentials footer */}
                <div className="mt-2 px-1 flex items-center justify-between text-[10px] text-sg-mute">
                  <span className="flex items-center gap-1.5">
                    <ShieldCheck className="w-3 h-3 text-emerald-600" />
                    <span>Grounded in RERA registries</span>
                  </span>
                  <span className="tabular-nums">{cap - used} messages left</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}


// ─── Message bubble with verified badge + source citations ────────────
function MessageBubble({ m, isAgentMode, agentName, isAdmin }) {
  if (m.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl px-4 py-2.5 text-sm bg-sg-navy text-white whitespace-pre-wrap leading-relaxed">
          {m.text}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div className={`max-w-[90%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${
        m.error
          ? "bg-red-50 border border-red-200 text-red-900"
          : "bg-white border border-sg-line text-sg-navy shadow-sm shadow-sg-navy/5"
      }`}>
        {/* Verified badge — 3 tiers: Grounded (Google Search) → Live (registry) → Shape-check → none */}
        {(m.verified || m.live_verified) && (
          <div className={`flex items-center gap-1.5 mb-1.5 pb-1.5 border-b ${m.live_verified ? "border-emerald-100" : "border-amber-100"}`}>
            <BadgeCheck className={`w-3.5 h-3.5 flex-shrink-0 ${m.live_verified ? "text-emerald-600" : "text-amber-600"}`} />
            <span className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${m.live_verified ? "text-emerald-700" : "text-amber-700"}`}>
              {m.grounded_hits > 0
                ? `Live-grounded · Google Search × ${m.grounded_hits}`
                : (m.live_verified
                  ? "Live-verified · Official registry"
                  : "Shape-checked · RERA format valid")}
            </span>
            {m.live_verified && (
              <span className="ml-auto inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
            )}
          </div>
        )}

        <div>{m.text}</div>

        {/* Per-project live-fetch status — ADMIN-ONLY (transparent about network outcomes) */}
        {isAdmin && m.live_fetch && m.live_fetch.length > 0 && (
          <div className="mt-2 pt-1.5 border-t border-sg-line/50 space-y-0.5" data-testid="registry-checks-admin">
            <div className="flex items-center justify-between">
              <div className="text-[9px] uppercase tracking-[0.2em] text-sg-mute font-semibold">Registry checks</div>
              <div className="text-[8px] uppercase tracking-[0.2em] text-sg-gold-deep font-semibold bg-sg-gold/10 px-1.5 py-0.5 rounded border border-sg-gold/30">Admin view</div>
            </div>
            {m.live_fetch.map((f, idx) => (
              <div key={idx} className="flex items-center gap-1.5 text-[10.5px] text-sg-navy/80">
                <span className={`inline-block h-1.5 w-1.5 rounded-full flex-shrink-0 ${
                  f.status === "ok" ? "bg-emerald-500"
                  : f.status === "unreachable" ? "bg-amber-500"
                  : f.status === "no_structured_data" ? "bg-slate-400"
                  : "bg-sg-line"
                }`} />
                <span className="font-mono text-[10px] truncate flex-1">{f.rera_id}</span>
                <span className="text-sg-mute">
                  {f.status === "ok" ? (f.cached ? "cached · verified" : "live · verified")
                    : f.status === "unreachable" ? "registry unreachable"
                    : f.status === "no_structured_data" ? "no structured data"
                    : f.status}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Source citations — compact, first 3 + '+N more' expander */}
        {m.citations && m.citations.length > 0 && (
          <CitationsList citations={m.citations} />
        )}

        {/* Agent attribution — subtle line on greeting only */}
        {m.greeting && isAgentMode && (
          <div className="mt-2 pt-2 border-t border-sg-line/60 text-[10px] text-sg-mute">
            Contact & site-visit bookings route to {agentName}.
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Compact Sources list — 3 by default + "+N more" expander ─────────
function CitationsList({ citations }) {
  // Deduplicate by (authority + rera_id/domain) — same MahaRERA · P521... doesn't need to repeat
  const seen = new Set();
  const dedup = [];
  for (const c of citations) {
    const key = `${c.authority}|${c.rera_id || c.domain || c.url}`;
    if (seen.has(key)) continue;
    seen.add(key);
    dedup.push(c);
  }
  const MAX_VISIBLE = 3;
  const [expanded, setExpanded] = useState(false);
  const overflow = dedup.length - MAX_VISIBLE;
  const shown = expanded ? dedup : dedup.slice(0, MAX_VISIBLE);

  return (
    <div className="mt-2.5 pt-2 border-t border-sg-line/60 space-y-1" data-testid="citations-list">
      <div className="text-[9px] uppercase tracking-[0.2em] text-sg-mute font-semibold">Sources</div>
      {shown.map((c, idx) => <CitationRow key={idx} c={c} idx={idx} />)}
      {overflow > 0 && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-[10.5px] text-sg-gold-deep hover:text-sg-navy font-medium hover:underline pl-4.5 transition-colors"
          data-testid="citations-expand"
        >
          + {overflow} more
        </button>
      )}
      {expanded && overflow > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="text-[10.5px] text-sg-mute hover:text-sg-navy font-medium hover:underline pl-4.5 transition-colors"
          data-testid="citations-collapse"
        >
          show less
        </button>
      )}
    </div>
  );
}

function truncateId(s, keep = 4) {
  if (!s) return "";
  return s.length > keep + 3 ? `${s.slice(0, keep)}...` : s;
}

function CitationRow({ c, idx }) {
  // Simple format: [Icon] MahaRERA · P521...
  const label = c.rera_id ? truncateId(c.rera_id) : (c.title || c.domain || c.state || "");
  return (
    <a
      href={c.url}
      target="_blank"
      rel="noreferrer noopener"
      title={c.rera_id || c.title || c.url}
      className="flex items-center gap-1.5 text-[11px] text-sg-navy/80 hover:text-sg-gold-deep transition-colors group"
      data-testid={`citation-${idx}`}
    >
      <ShieldCheck className={`w-3 h-3 flex-shrink-0 ${c.grounded || c.live_verified ? "text-emerald-600" : (c.verified ? "text-sg-navy" : "text-amber-500")}`} />
      <span className="font-medium">{c.authority}</span>
      <span className="text-sg-mute">·</span>
      <span className="truncate flex-1 font-mono text-[10.5px]">{label}</span>
      {c.grounded && (
        <span className="text-[8.5px] uppercase tracking-wider bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full border border-emerald-200 whitespace-nowrap">
          Google
        </span>
      )}
      {!c.grounded && c.live_verified && (
        <span className="text-[8.5px] uppercase tracking-wider bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full border border-emerald-200 whitespace-nowrap">
          Live
        </span>
      )}
      <ExternalLink className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
    </a>
  );
}


// ─── Multi-step lead capture (Name → Phone → WhatsApp) ────────────────
function LeadFlow({ step, form, setForm, onAdvance, contactName }) {
  const cfg = {
    name:     { icon: UserIcon,       label: "Your name",                testId: "inprop-ai-lead-name",     type: "text", key: "name",     placeholder: "e.g. Rohan Sharma" },
    phone:    { icon: Phone,          label: "10-digit phone number",    testId: "inprop-ai-lead-phone",    type: "tel",  key: "phone",    placeholder: "e.g. 9876543210" },
    whatsapp: { icon: MessageSquare,  label: "WhatsApp (or type 'same')", testId: "inprop-ai-lead-whatsapp", type: "tel",  key: "whatsapp", placeholder: "same, or a different number" },
  }[step];
  const Icon = cfg.icon;

  return (
    <div className="bg-gradient-to-br from-sg-gold/8 to-sg-gold/[0.03] border border-sg-gold/40 rounded-2xl p-3.5 space-y-2.5" data-testid="inprop-ai-lead-form">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-sg-gold-deep font-semibold flex items-center gap-1.5">
          <ShieldCheck className="w-3 h-3" />
          Step {["name","phone","whatsapp"].indexOf(step) + 1} of 3
        </div>
        <div className="flex gap-1">
          {["name","phone","whatsapp"].map((s) => (
            <span
              key={s}
              className={`h-1 w-6 rounded-full ${step === s ? "bg-sg-gold" : (["name","phone","whatsapp"].indexOf(s) < ["name","phone","whatsapp"].indexOf(step) ? "bg-sg-gold/60" : "bg-sg-line")}`}
            />
          ))}
        </div>
      </div>
      <div className="text-xs text-sg-navy/80">
        {step === "name" && `Great — I'll set up a callback with ${contactName || "an authorised agent"}. What's your name?`}
        {step === "phone" && `Thanks ${form.name || "there"}. What's the best number to reach you on?`}
        {step === "whatsapp" && `And your WhatsApp — same as ${form.phone}, or a different one?`}
      </div>
      <div className="flex items-center gap-2 bg-white rounded-lg border border-sg-line px-3 py-2 focus-within:border-sg-gold-deep">
        <Icon className="w-3.5 h-3.5 text-sg-mute flex-shrink-0" />
        <input
          type={cfg.type}
          autoFocus
          value={form[cfg.key]}
          onChange={(e) => setForm((f) => ({ ...f, [cfg.key]: e.target.value }))}
          onKeyDown={(e) => { if (e.key === "Enter") onAdvance(); }}
          placeholder={cfg.placeholder}
          className="flex-1 text-sm bg-transparent focus:outline-none text-sg-navy placeholder:text-sg-mute"
          data-testid={cfg.testId}
        />
      </div>
      <button
        type="button"
        onClick={onAdvance}
        className="w-full h-9 rounded-full bg-sg-navy hover:bg-sg-navy/90 text-white text-xs font-semibold uppercase tracking-[0.14em]"
        data-testid="inprop-ai-lead-next"
      >
        {step === "whatsapp" ? "Share with agent" : "Continue"}
      </button>
    </div>
  );
}



// ─── Site visit booking flow (Date → Time → Contact → Confirm) ────────
const TIME_SLOTS = [
  "10:00 AM", "11:30 AM", "1:00 PM", "2:30 PM", "4:00 PM", "5:30 PM",
];

function toISODate(d) {
  if (!d) return "";
  // Local YYYY-MM-DD (avoid UTC shift that .toISOString() introduces).
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function formatDatePretty(d) {
  if (!d) return "";
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

function VisitFlow({ step, form, setForm, busy, availability, onAdvance, onBack, onCancel, contactName }) {
  const steps = ["date", "time", "contact", "confirm"];
  const idx = steps.indexOf(step);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 60);

  const allowedWeekdays = availability?.weekdays || [1, 2, 3, 4, 5, 6];
  const blackoutSet = new Set(availability?.blackout_dates || []);
  const allowedSlots = (availability?.time_slots && availability.time_slots.length > 0)
    ? availability.time_slots
    : TIME_SLOTS;

  const isDisabled = (d) => {
    if (d < today || d > maxDate) return true;
    if (!allowedWeekdays.includes(d.getDay())) return true;
    if (blackoutSet.has(toISODate(d))) return true;
    return false;
  };

  return (
    <div className="bg-gradient-to-br from-sg-navy/[0.04] to-sg-gold/[0.05] border border-sg-gold/40 rounded-2xl p-3.5 space-y-2.5" data-testid="inprop-ai-visit-form">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-sg-gold-deep font-semibold flex items-center gap-1.5">
          <CalendarIcon className="w-3 h-3" />
          Book Site Visit · Step {idx + 1} of 4
        </div>
        <div className="flex gap-1">
          {steps.map((s, i) => (
            <span
              key={s}
              className={`h-1 w-5 rounded-full ${i === idx ? "bg-sg-gold" : (i < idx ? "bg-sg-gold/60" : "bg-sg-line")}`}
            />
          ))}
        </div>
      </div>

      {/* STEP: DATE */}
      {step === "date" && (
        <div className="space-y-2">
          <div className="text-xs text-sg-navy/80">
            When would you like to visit? {contactName ? `${contactName} will meet you at the property.` : "An authorised advisor will meet you at the property."}
          </div>
          <div className="bg-white rounded-lg border border-sg-line p-1 flex justify-center overflow-x-auto">
            <Calendar
              mode="single"
              selected={form.date}
              onSelect={(d) => setForm((f) => ({ ...f, date: d }))}
              disabled={isDisabled}
              initialFocus
              data-testid="inprop-ai-visit-calendar"
            />
          </div>
          {form.date && (
            <div className="text-[11px] text-sg-navy/70 pl-1">
              Selected: <span className="font-medium text-sg-navy">{formatDatePretty(form.date)}</span>
            </div>
          )}
          {availability && (availability.blackout_dates?.length || availability.weekdays?.length < 7) ? (
            <div className="text-[10px] text-sg-mute pl-1">
              Only available days shown{availability.blackout_dates?.length ? ` · ${availability.blackout_dates.length} blackout date${availability.blackout_dates.length === 1 ? "" : "s"}` : ""}.
            </div>
          ) : null}
        </div>
      )}

      {/* STEP: TIME */}
      {step === "time" && (
        <div className="space-y-2">
          <div className="text-xs text-sg-navy/80">
            Pick a time slot for <span className="font-medium">{formatDatePretty(form.date)}</span>.
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {allowedSlots.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setForm((f) => ({ ...f, time: t }))}
                className={`h-9 rounded-lg border text-[11px] font-medium flex items-center justify-center gap-1 transition-colors ${
                  form.time === t
                    ? "bg-sg-navy border-sg-navy text-white"
                    : "bg-white border-sg-line text-sg-navy hover:border-sg-gold"
                }`}
                data-testid={`inprop-ai-visit-time-${t.replace(/[:\s]/g, "").toLowerCase()}`}
              >
                <Clock className="w-3 h-3" />
                {t}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* STEP: CONTACT */}
      {step === "contact" && (
        <div className="space-y-2">
          <div className="text-xs text-sg-navy/80">
            Great — where should {contactName || "our advisor"} confirm your visit?
          </div>
          {[
            { key: "name",     placeholder: "Your name",              icon: UserIcon,     testId: "inprop-ai-visit-name",     type: "text" },
            { key: "phone",    placeholder: "10-digit phone",         icon: Phone,        testId: "inprop-ai-visit-phone",    type: "tel"  },
            { key: "whatsapp", placeholder: "WhatsApp (or 'same')",   icon: MessageSquare, testId: "inprop-ai-visit-whatsapp", type: "tel"  },
          ].map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.key} className="flex items-center gap-2 bg-white rounded-lg border border-sg-line px-3 py-2 focus-within:border-sg-gold-deep">
                <Icon className="w-3.5 h-3.5 text-sg-mute flex-shrink-0" />
                <input
                  type={f.type}
                  value={form[f.key]}
                  onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  className="flex-1 text-sm bg-transparent focus:outline-none text-sg-navy placeholder:text-sg-mute"
                  data-testid={f.testId}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* STEP: CONFIRM */}
      {step === "confirm" && (
        <div className="space-y-2">
          <div className="text-xs text-sg-navy/80">Confirm your visit:</div>
          <div className="bg-white rounded-lg border border-sg-line p-3 text-xs space-y-1.5 text-sg-navy">
            <Row label="Date"     value={formatDatePretty(form.date)} icon={CalendarIcon} />
            <Row label="Time"     value={form.time}                    icon={Clock} />
            <Row label="Name"     value={form.name}                    icon={UserIcon} />
            <Row label="Phone"    value={form.phone}                   icon={Phone} />
            <Row label="WhatsApp" value={form.whatsapp || "same as phone"} icon={MessageSquare} />
            {contactName && (
              <div className="pt-2 mt-1 border-t border-sg-line text-[10px] text-sg-mute">
                Booking routes to <span className="font-medium text-sg-navy">{contactName}</span>&apos;s dashboard.
              </div>
            )}
          </div>
          <textarea
            value={form.notes}
            onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
            placeholder="Any notes for the advisor? (optional)"
            rows={2}
            className="w-full text-xs bg-white border border-sg-line rounded-lg px-3 py-2 focus:outline-none focus:border-sg-gold-deep resize-none"
            data-testid="inprop-ai-visit-notes"
          />
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-1">
        {step !== "date" && (
          <button
            type="button"
            onClick={onBack}
            disabled={busy}
            className="h-9 px-3 rounded-full border border-sg-line text-sg-navy text-xs font-medium hover:bg-sg-cream-soft/50 disabled:opacity-50 flex items-center gap-1"
            data-testid="inprop-ai-visit-back"
          >
            <ChevronLeft className="w-3 h-3" /> Back
          </button>
        )}
        <button
          type="button"
          onClick={onAdvance}
          disabled={busy}
          className="flex-1 h-9 rounded-full bg-sg-navy hover:bg-sg-navy/90 text-white text-xs font-semibold uppercase tracking-[0.14em] disabled:opacity-60 flex items-center justify-center gap-2"
          data-testid="inprop-ai-visit-next"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (step === "confirm" ? <CheckCircle2 className="w-3.5 h-3.5" /> : null)}
          {step === "confirm" ? "Confirm booking" : "Continue"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="text-[10px] text-sg-mute hover:text-sg-navy px-2 disabled:opacity-50"
          data-testid="inprop-ai-visit-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function Row({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center gap-2">
      {Icon && <Icon className="w-3 h-3 text-sg-mute flex-shrink-0" />}
      <span className="text-[10px] uppercase tracking-wider text-sg-mute w-16">{label}</span>
      <span className="font-medium text-sg-navy truncate">{value}</span>
    </div>
  );
}
